"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useT } from "@/lib/i18n";

/**
 * App-wide 404. Next only routes UNMATCHED URLs to the ROOT not-found (nested not-found.tsx
 * fire only on an explicit notFound() call), so this single file must serve both the staff app
 * and the customer portal. We branch on the path (client-side, per Next's not-found docs):
 * under /p/** show a portal-appropriate 404 (English, links that stay inside the portal and
 * never hand off to the staff dashboard/login); everywhere else, the staff 404.
 */
export default function NotFound() {
  const t = useT();
  const pathname = usePathname() ?? "";
  const isPortal = pathname === "/p" || pathname.startsWith("/p/");

  if (isPortal) {
    // Resolved portal base /p/{org}/{portal} → offer in-portal links; otherwise → portals landing.
    const base = pathname.match(/^(\/p\/[^/]+\/[^/]+)/)?.[1] ?? null;
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--page-bg)" }}>
        <div className="text-center px-6">
          <p className="text-6xl font-bold" style={{ color: "var(--primary-ink)" }}>404</p>
          <h1 className="mt-4 text-xl font-semibold" style={{ color: "var(--text-primary)" }}>
            {t("common.pageNotFound")}
          </h1>
          <p className="mt-2 text-base" style={{ color: "var(--text-muted)" }}>
            {t("common.pageNotFoundHint")}
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            {base ? (
              <>
                <Link href={base} className="px-4 py-2 rounded-lg text-base font-medium text-white" style={{ backgroundColor: "var(--primary)" }}>
                  {t("common.backToDashboard")}
                </Link>
                <Link href={`${base}/jobs/tickets`} className="px-4 py-2 rounded-lg text-base font-medium border" style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}>
                  {t("ops.backToTickets")}
                </Link>
              </>
            ) : (
              <Link href="/p" className="px-4 py-2 rounded-lg text-base font-medium text-white" style={{ backgroundColor: "var(--primary)" }}>
                {t("portal.goToMyPortals")}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Staff 404 (unchanged behaviour)
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--page-bg)" }}>
      <div className="text-center px-6">
        <p className="text-6xl font-bold" style={{ color: "var(--primary-ink)" }}>404</p>
        <h1 className="mt-4 text-xl font-semibold" style={{ color: "var(--text-primary)" }}>
          {t("notFound.title")}
        </h1>
        <p className="mt-2 text-base" style={{ color: "var(--text-muted)" }}>
          {t("notFound.body")}
        </p>
        <div className="mt-6 flex gap-2 justify-center">
          <Link href="/dashboard" className="px-4 py-2 rounded-lg text-base font-medium text-white" style={{ backgroundColor: "var(--primary)" }}>
            {t("err.toDashboard")}
          </Link>
          <Link href="/" className="px-4 py-2 rounded-lg text-base font-medium border" style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}>
            {t("common.home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
